// Queuing tab state. It lives above the tab rather than inside it because
// switching tabs unmounts the panel, and losing a table of hand-entered staff
// counts on a stray click would be worse than the extra prop.
//
// The request itself is built by the caller, which is the side that holds the
// scenario. That keeps one build per render instead of one for the run and
// another for the staleness check, and leaves this hook responsible only for the
// call and what came back.

import React from "react";
import { postQueuingAnalysis } from "../services/whatIfService";
import { normalizeQueueingResponse } from "../utils/whatif/whatIfQueueing";

export const QUEUEING_STATUS = {
  idle: "idle",
  loading: "loading",
  ready: "ready",
  error: "error",
};

// Results describe one exact set of inputs. Once any of them moves, the numbers
// on screen are answering a question the user is no longer asking, so the run is
// fingerprinted and the table is flagged stale rather than silently misleading.
export const fingerprintRequest = (request) => JSON.stringify(request);

export default function useWhatIfQueueing(recordedArrivalPerWeek) {
  // Seeded from the demand recorded on the process, so the tab opens ready to
  // run. It stays editable — the whole point is asking "what if demand were
  // different" — and only seeds the initial render, never overwriting a number
  // the user has since typed.
  const [arrivalPerWeek, setArrivalPerWeek] = React.useState(
    recordedArrivalPerWeek != null && recordedArrivalPerWeek > 0
      ? String(recordedArrivalPerWeek)
      : "",
  );
  const [staffByJob, setStaffByJob] = React.useState({});
  const [status, setStatus] = React.useState(QUEUEING_STATUS.idle);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [ranWith, setRanWith] = React.useState(null);

  const setStaff = React.useCallback((jobId, value) => {
    setStaffByJob((current) => ({ ...current, [String(jobId)]: value }));
  }, []);

  const reset = React.useCallback(() => {
    setStaffByJob({});
    setResult(null);
    setError(null);
    setRanWith(null);
    setStatus(QUEUEING_STATUS.idle);
  }, []);

  const run = React.useCallback(async (request) => {
    setStatus(QUEUEING_STATUS.loading);
    setError(null);

    try {
      const payload = await postQueuingAnalysis(request);
      setResult(normalizeQueueingResponse(payload));
      setRanWith(fingerprintRequest(request));
      setStatus(QUEUEING_STATUS.ready);
    } catch (caught) {
      console.error("Queuing analysis failed:", caught);
      setError(caught?.message || "Unable to run the queuing analysis.");
      setStatus(QUEUEING_STATUS.error);
    }
  }, []);

  // An arrival rate is the one input with no sensible default — the whole
  // analysis is "at this demand", so there is nothing to compute without it.
  const arrival = Number(arrivalPerWeek);
  const canRun =
    Number.isFinite(arrival) && arrival > 0 && status !== QUEUEING_STATUS.loading;

  return {
    arrivalPerWeek,
    setArrivalPerWeek,
    staffByJob,
    setStaff,
    status,
    result,
    error,
    ranWith,
    canRun,
    run,
    reset,
  };
}
